import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GradButton } from "@/components/erp";
import api, { errMsg } from "@/lib/api";

const DURUM = { guncelle: ["Güncellenecek", "text-sky-300"], atla: ["Atlanacak", "text-muted-foreground"], hata: ["Hatalı", "text-rose-300"] };

const Ozet = ({ o }) => (
  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm" data-testid="ra-ozet">
    {[["Güncellenecek ürün", o.guncelle, "ra-guncelle"], ["Reçetesi değişen", o.recete, "ra-recete"], ["Operasyonu değişen", o.operasyon, "ra-operasyon"], ["Hatalı", o.hata, "ra-hata"], ["Atlanacak", o.atla, "ra-atla"]].map(([l, v, t]) => (
      <div key={t} className="rounded-xl bg-white/5 p-3"><div className="text-[11px] text-muted-foreground">{l}</div><div className="font-semibold font-mono" data-testid={t}>{v ?? 0}</div></div>))}
  </div>
);

const Tablo = ({ satirlar }) => (
  <div className="overflow-x-auto max-h-[40vh] erp-scroll rounded-xl border border-white/10"><table className="w-full text-xs" data-testid="ra-tablo">
    <thead className="sticky top-0 bg-[var(--card,#111)]"><tr className="text-left text-muted-foreground"><th className="p-2">Satır</th><th className="p-2">Stok kodu</th><th className="p-2">Reçete</th><th className="p-2">Operasyon</th><th className="p-2">Durum</th></tr></thead>
    <tbody>{satirlar.map((r) => (
      <tr key={r.satir} className="border-t border-white/5" data-testid={`ra-satir-${r.satir}`}>
        <td className="p-2 font-mono">{r.satir}</td><td className="p-2"><div className="font-mono">{r.kod}</div><div className="text-muted-foreground truncate max-w-[240px]">{r.urun_ad}</div></td>
        <td className="p-2">{r.recete || "—"}</td><td className="p-2">{r.operasyon || "—"}</td>
        <td className={`p-2 ${DURUM[r.durum][1]}`}>{DURUM[r.durum][0]}{r.mesaj ? ` · ${r.mesaj}` : ""}</td>
      </tr>))}</tbody>
  </table></div>
);

export const ReceteAktarim = ({ onDone }) => {
  const [acik, setAcik] = useState(false);
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const indir = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/recete-aktarim/sablon", { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = "recete-operasyon-sablonu.xlsx"; a.click();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const yukle = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("dosya", f);
    try { setOn((await api.post("/recete-aktarim/onizle", fd)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  const uygula = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/recete-aktarim/uygula", { token: on.token });
      toast.success(`${data.urun} ürün güncellendi (${data.recete} reçete, ${data.operasyon} operasyon)`); setOn(null); setAcik(false); onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid="recete-aktar" onClick={() => setAcik(true)} className="inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm border border-white/15 hover:bg-white/10 transition-colors"><FileSpreadsheet className="w-4 h-4" />Reçete/Operasyon Excel</button>
      {acik && <Dialog open onOpenChange={(o) => !o && !busy && (setAcik(false), setOn(null))}>
        <DialogContent className="glass max-w-4xl max-h-[90vh] overflow-y-auto erp-scroll" data-testid="recete-aktar-dialog">
          <DialogHeader><DialogTitle className="font-head">Excel ile toplu reçete ve operasyon</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Şablon tüm mamul ve yarı mamullerin mevcut reçete ve operasyonlarıyla dolu gelir; ürün başına tek satır. Düzeltip geri yükleyin: dolu bölüm ürünün reçetesini/operasyonlarını tamamen değiştirir, boş bölüm değişmez, "-" siler. Yalnızca değişen ürünler aktarılır.</p>
          <div className="flex flex-wrap gap-2">
            <button data-testid="ra-sablon" disabled={busy} onClick={indir} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm border border-white/15 hover:bg-white/5"><Download className="w-4 h-4" />Şablonu indir (dolu)</button>
            <GradButton data-testid="ra-dosya-sec" disabled={busy} onClick={() => ref.current.click()}><Upload className="w-4 h-4" />{busy && !on ? "İşleniyor…" : "Dosya seç ve önizle"}</GradButton>
            <input ref={ref} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="ra-dosya" onChange={(e) => yukle(e.target.files?.[0])} />
          </div>
          {on && <>
            <Ozet o={on.ozet} />
            <Tablo satirlar={on.satirlar} />
            {on.satirlar.length >= 1000 && <p className="text-[11px] text-muted-foreground">İlk 1000 satır gösteriliyor; tümü uygulanır.</p>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setOn(null)} className="px-5 py-2 rounded-full border border-white/10 text-sm" data-testid="ra-vazgec">Vazgeç</button>
              <GradButton data-testid="ra-onayla" disabled={busy || !on.ozet.guncelle} onClick={uygula}><Check className="w-4 h-4" />{busy ? "Aktarılıyor…" : `${on.ozet.guncelle} ürünü güncelle`}</GradButton>
            </div>
          </>}
        </DialogContent>
      </Dialog>}
    </>
  );
};
